import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { useAuthData, useCreateHub, useSetMember, useUpdateAccountPreferences } from '@/hooks';
import { LANDING_FILTER_STORAGE_KEY } from '@/utils/auth-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { Building2, Loader2, User } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { HubBrandingSection } from './HubBrandingSection';
import { SettingsPage, SettingsRow, SettingsRowList, SettingsSection, SettingsSectionBody } from './SettingsPage';

const createHubSchema = z.object({
  name: z.string().trim().min(1, 'Hub name is required'),
  domain: z.enum(['CONSTRUCTION', 'MEDICAL']),
  phone: z.string().trim().optional(),
  website: z.string().trim().optional(),
});

type CreateHubFormData = z.infer<typeof createHubSchema>;

export function HubsSection() {
  const { account, member, members } = useAuthData();
  const createHubMutation = useCreateHub();
  const setMemberMutation = useSetMember();
  const updatePreferencesMutation = useUpdateAccountPreferences();

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateHubFormData>({
    resolver: zodResolver(createHubSchema),
    mode: 'onBlur',
    defaultValues: { domain: 'CONSTRUCTION' },
  });

  const handleSwitch = (memberId: number) => {
    const targetMember = members?.find((m) => m.id === memberId);
    setMemberMutation.mutate({ id: memberId }, {
      onSuccess: () => {
        // Product decision: a configured landing view makes switching a
        // "take me to what matters" action; without one, switching stays in place.
        if (targetMember && targetMember.landingFilterId !== null) {
          sessionStorage.setItem(LANDING_FILTER_STORAGE_KEY, String(targetMember.landingFilterId));
          window.location.assign('/explore');
          return;
        }
        window.location.reload();
      },
    });
  };

  const onSubmit = (data: CreateHubFormData) => {
    createHubMutation.mutate(
      {
        name: data.name,
        domain: data.domain,
        phone: data.phone || undefined,
        website: data.website || undefined,
      },
      {
        onSuccess: () => {
          reset();
        },
      }
    );
  };

  return (
    <SettingsPage
      title="Hubs"
      description="Switch between your hubs, create a corporate hub and manage this hub's letterhead."
    >
      <SettingsSection title="Your hubs">
        <SettingsRowList>
          {members?.map((m) => (
            <SettingsRow
              key={m.id}
              leading={
                m.isPersonal ? (
                  <User className="h-4 w-4 shrink-0 text-muted-foreground" />
                ) : (
                  <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                )
              }
              title={m.hubName}
              meta={
                m.isPersonal ? (
                  <Badge variant="secondary">Personal</Badge>
                ) : m.isAdmin ? (
                  <Badge variant="outline">Admin</Badge>
                ) : null
              }
              detail={m.domain}
              trailing={
                member?.id === m.id ? (
                  <Badge variant="secondary">Current</Badge>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSwitch(m.id)}
                    disabled={setMemberMutation.isPending}
                  >
                    Switch
                  </Button>
                )
              }
            />
          ))}
          <SettingsRow
            title={<Label htmlFor="hubs-hide-personal">Hide personal in member switcher</Label>}
            detail="Personal hubs stay available at no extra cost. This only hides them from the avatar menu."
            trailing={
              <Switch
                id="hubs-hide-personal"
                data-testid="hubs-hide-personal-toggle"
                checked={account?.hidePersonalMembers ?? false}
                onCheckedChange={(checked) => updatePreferencesMutation.mutate({ hidePersonalMembers: checked })}
                disabled={updatePreferencesMutation.isPending}
              />
            }
          />
        </SettingsRowList>
      </SettingsSection>

      <SettingsSection
        title="Create corporate hub"
        description="A shared workspace. You become its admin and can invite members."
      >
        <SettingsSectionBody>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 max-w-md">
            <div>
              <Input
                data-testid="create-hub-name"
                placeholder="Hub name"
                aria-label="Hub name"
                {...register('name')}
                disabled={createHubMutation.isPending}
              />
              {errors.name && (
                <p className="text-destructive text-sm mt-1">{errors.name.message}</p>
              )}
            </div>

            <div>
              <Label className="text-sm text-muted-foreground">Industry</Label>
              <Controller
                control={control}
                name="domain"
                render={({ field }) => (
                  <RadioGroup
                    value={field.value}
                    onValueChange={field.onChange}
                    className="flex gap-4 mt-1"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="CONSTRUCTION" id="hub-domain-construction" />
                      <Label htmlFor="hub-domain-construction" className="cursor-pointer">Construction</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="MEDICAL" id="hub-domain-medical" />
                      <Label htmlFor="hub-domain-medical" className="cursor-pointer">Medical</Label>
                    </div>
                  </RadioGroup>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Input
                placeholder="Phone (optional)"
                aria-label="Phone"
                {...register('phone')}
                disabled={createHubMutation.isPending}
              />
              <Input
                placeholder="Website (optional)"
                aria-label="Website"
                {...register('website')}
                disabled={createHubMutation.isPending}
              />
            </div>

            <Button
              data-testid="create-hub-button"
              type="submit"
              disabled={createHubMutation.isPending}
            >
              {createHubMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating hub…
                </>
              ) : (
                'Create hub'
              )}
            </Button>
          </form>
        </SettingsSectionBody>
      </SettingsSection>

      <HubBrandingSection />
    </SettingsPage>
  );
}
